import { onMounted, onUnmounted, useId, type Component, type MaybeRef } from 'vue';
import type { ComponentExposed, ComponentProps } from 'vue-component-type-helpers';
import { DialogSystem } from './DialogSystem';
import type { DialogQueueOptions } from './dialogQueue';

export type ComponentWithShow<T extends Component> =
  ComponentExposed<T> extends { show: (...args: any) => void } ? T : never;

export function useDialog<T extends Component>(options: {
  component: ComponentWithShow<T>;
  /** Make sure this is a computed value if you want deep reactivity */
  props: MaybeRef<ComponentProps<T>>;
}): {
  show: ComponentExposed<ComponentWithShow<T>>['show'];
  showQueued: (
    queueOptions: DialogQueueOptions,
    ...args: Parameters<ComponentExposed<ComponentWithShow<T>>['show']>
  ) => Promise<Awaited<ReturnType<ComponentExposed<ComponentWithShow<T>>['show']>> | undefined>;
  hide: () => void;
} {
  const id = useId();
  onMounted(() => {
    DialogSystem.addDialog?.(id, options.component, options.props);
  });
  onUnmounted(() => {
    DialogSystem.removeDialog?.(id);
  });
  return {
    show: (...arg: any) => {
      return DialogSystem.show?.(id, arg) as ReturnType<ComponentExposed<ComponentWithShow<T>>['show']>;
    },
    showQueued: (queueOptions: DialogQueueOptions, ...arg: any) => {
      return DialogSystem.showQueued?.(id, arg, queueOptions) as Promise<
        Awaited<ReturnType<ComponentExposed<ComponentWithShow<T>>['show']>> | undefined
      >;
    },
    hide: () => {
      void DialogSystem.hide?.(id);
    },
  };
}
